import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, Phone } from "lucide-react";
import axios from "axios";
import { Reveal, SectionHead } from "../components/Reveal";
import Seo from "../components/Seo";
import { SiteMarquee } from "../components/SiteMarquee";
import { SERVICES, API, PHONE_LINK } from "../data";

const HERO_IMG = "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85";
const HERO_LINES = ["Costruiamo", "il futuro", "del tuo spazio"];

const STATS = [
  { value: "25+", label: "Anni di Esperienza" },
  { value: "450+", label: "Cantieri Completati" },
  { value: "100%", label: "Impianti Certificati" },
];

const Hero = () => {
  const ref = useRef(null);
  const [heroImg, setHeroImg] = useState(HERO_IMG);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.2]);

  useEffect(() => {
    axios
      .get(`${API}/foto`)
      .then((r) => {
        if (r.data.length) {
          const pick = r.data.find((f) => f.titolo.includes("Illuminazione Esterna")) || r.data[0];
          setHeroImg(`${API}/foto/file/${pick.storage_path}`);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          key={heroImg}
          src={heroImg}
          alt="Lavoro reale Edilkrea: parete in marmo nero e velette LED"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          className="h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-16 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-gold"
          data-testid="hero-overline"
        >
          Edilkrea Technoimpianti — Costruzioni & Impianti
        </motion.p>

        <h1 className="mt-6 font-display font-extrabold uppercase tracking-tight leading-[0.95] text-[7.5vw] sm:text-6xl lg:text-7xl text-cream" data-testid="hero-title">
          {HERO_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 1 ? <span className="text-gold">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          Ristrutturazioni chiavi in mano, impiantistica tecnologica e nuove costruzioni.
          Dalla progettazione alla consegna: un unico partner, zero pensieri.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contatti"
            data-testid="hero-quote-button"
            className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-semibold uppercase tracking-wider text-ink transition-all hover:bg-copper hover:-translate-y-0.5"
          >
            Richiedi Preventivo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/galleria"
            data-testid="hero-gallery-button"
            className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-7 py-4 text-sm font-semibold uppercase tracking-wider text-cream transition-all hover:border-gold hover:text-gold hover:-translate-y-0.5"
          >
            Esplora i Progetti
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-14 grid grid-cols-3 gap-4 border-t border-cream/10 pt-8 max-w-2xl"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display text-2xl sm:text-4xl font-bold text-gold">{s.value}</div>
              <div className="mt-1 text-[11px] sm:text-xs uppercase tracking-widest text-slate-400">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

const BentoServices = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" data-testid="home-services-bento">
    <div className="flex flex-wrap items-end justify-between gap-6">
      <SectionHead overline="Cosa Facciamo" title="Servizi d'Eccellenza" />
      <Reveal delay={0.15}>
        <Link to="/servizi" data-testid="bento-all-services-link" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gold hover:text-copper transition-colors">
          Tutti i servizi <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>

    <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4 md:auto-rows-[230px]">
      {SERVICES.map((s, i) => (
        <Reveal
          key={s.id}
          delay={i * 0.08}
          className={
            i === 0
              ? "md:col-span-7 md:row-span-2"
              : i === 1
              ? "md:col-span-5"
              : i === 2
              ? "md:col-span-5"
              : "md:col-span-12"
          }
        >
          <Link
            to="/servizi"
            data-testid={`bento-service-${s.id}`}
            className="group relative block h-full min-h-[230px] overflow-hidden rounded-2xl border border-gold/15"
          >
            <img
              src={s.image}
              alt={s.title}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-copper">{s.tag}</p>
              <h3 className="mt-2 font-display text-xl sm:text-2xl font-bold uppercase text-cream leading-tight">
                {s.title}
              </h3>
            </div>
            <span className="absolute top-5 right-5 flex h-10 w-10 items-center justify-center rounded-full bg-ink/60 backdrop-blur border border-gold/20 text-gold opacity-0 transition-opacity group-hover:opacity-100">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const GalleryStrip = () => {
  const [foto, setFoto] = useState([]);
  useEffect(() => {
    axios.get(`${API}/foto`).then((r) => setFoto(r.data.slice(0, 8))).catch(() => {});
  }, []);
  if (foto.length === 0) return null;

  return (
    <section className="bg-panel border-y border-gold/10 py-24 sm:py-32 overflow-hidden" data-testid="home-gallery-strip">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead overline="Portfolio" title="Lavori in Vetrina" />
          <Reveal delay={0.15}>
            <Link to="/galleria" data-testid="strip-gallery-link" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gold hover:text-copper transition-colors">
              Vedi la galleria <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
      <Reveal className="mt-12" y={40}>
        <div className="flex gap-4 overflow-x-auto px-5 sm:px-8 pb-4 snap-x snap-mandatory [scrollbar-width:thin]">
          {foto.map((f, i) => (
            <Link
              to="/galleria"
              key={f.id}
              data-testid={`strip-project-${i}`}
              className="group relative h-80 w-72 sm:w-80 shrink-0 snap-start overflow-hidden rounded-2xl border border-gold/15"
            >
              <img src={`${API}/foto/file/${f.storage_path}`} alt={f.titolo} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-copper">{f.categoria}</p>
                <h3 className="mt-1 font-display text-lg font-bold uppercase text-cream">{f.titolo}</h3>
              </div>
            </Link>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const ReviewsTeaser = () => {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    axios.get(`${API}/recensioni`).then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" data-testid="home-reviews-teaser">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHead overline="Testimonianze" title="Parola ai Clienti" />
        <Reveal delay={0.15}>
          <Link to="/recensioni" data-testid="teaser-reviews-link" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gold hover:text-copper transition-colors">
            Tutte le recensioni <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {reviews.map((r, i) => (
          <Reveal key={r.id} delay={i * 0.1}>
            <figure className="h-full rounded-2xl border border-gold/15 bg-card2 p-6 transition-colors hover:border-gold/40">
              <div className="flex gap-1" aria-label={`${r.rating} stelle`}>
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className={`h-4 w-4 ${s < r.rating ? "fill-gold text-gold" : "text-slate-600"}`} />
                ))}
              </div>
              <blockquote className="mt-4 text-sm leading-relaxed text-slate-300">"{r.testo}"</blockquote>
              <figcaption className="mt-5 flex items-center justify-between">
                <span className="font-display font-bold text-cream">{r.nome}</span>
                <span className="font-mono text-[11px] uppercase tracking-widest text-copper">{r.servizio}</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const CtaBanner = () => (
  <section className="relative overflow-hidden border-t border-gold/10" data-testid="home-cta-banner">
    <div className="absolute inset-0 bg-gradient-to-br from-panel via-ink to-panel" />
    <div className="pointer-events-none absolute -top-24 right-0 select-none font-display text-[18vw] leading-none font-extrabold uppercase text-stroke-gold opacity-30">
      KREA
    </div>
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 text-center">
      <Reveal>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-gold">Preventivo gratuito e senza impegno</p>
        <h2 className="mx-auto mt-6 max-w-3xl font-display text-3xl sm:text-5xl font-bold uppercase leading-none text-cream">
          Hai un progetto in mente? <span className="text-copper">Realizziamolo insieme.</span>
        </h2>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/contatti"
            data-testid="cta-quote-button"
            className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-wider text-ink transition-all hover:bg-copper hover:-translate-y-0.5"
          >
            Richiedi Preventivo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={PHONE_LINK}
            data-testid="cta-phone-button"
            className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-8 py-4 text-sm font-semibold uppercase tracking-wider text-cream transition-all hover:border-gold hover:text-gold hover:-translate-y-0.5"
          >
            <Phone className="h-4 w-4" /> Chiamaci Ora
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo
        title="Ristrutturazioni Chiavi in Mano | Edilkrea Technoimpianti"
        description="Edilkrea Technoimpianti: ristrutturazioni chiavi in mano, impianti elettrici e idraulici, nuove costruzioni. Preventivo gratuito: 338 280 0207."
      />
      <Hero />
      <SiteMarquee />
      <BentoServices />
      <GalleryStrip />
      <ReviewsTeaser />
      <CtaBanner />
    </main>
  );
}
